/* Home device finder band (sections/device-finder-band.liquid), a three-step wizard:
 * 1 device type tiles (native radios) → 2 screen size / iPhone line, then the models of that size
 *   (built from the `device` metaobjects, the same list as /pages/find-your-device, fetched lazily as
 *   ?view=device-data, newest first), or the model number matched as typed (same matching as the
 *   finder page) → 3 the pieces cut for it, read from one JSON map (/collections/all?view=finder-list) into a
 *   two-column list (thumbnail, name, type, price; the first 6 per filter) with type filters and a
 *   gold button to the full finder (?model=…).
 * The chosen device is remembered in localStorage 'harber:device' { name, models, handles, family }:
 * returning visitors land on step 3, and tech product pages mark and preselect the size that fits
 * (assets/product.js). */
(() => {
  const KEY = 'harber:device';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  const store = {
    get() { try { const v = JSON.parse(localStorage.getItem(KEY) || 'null'); return v && v.name ? v : null; } catch (e) { return null; } },
    set(v) { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* private mode */ } },
    clear() { try { localStorage.removeItem(KEY); } catch (e) { /* private mode */ } },
  };
  const norm = (v) => String(v || '').toUpperCase().replace(/\s+/g, '');
  const key = (v) => String(v || '').toLowerCase().normalize('NFD').replace(/[^a-z0-9]/g, '');
  /* `t` HTML-escapes the strings (doesn&#39;t): decode them for textContent */
  const decode = (v) => { const ta = document.createElement('textarea'); ta.innerHTML = v; return ta.value; };
  const fill = (s, map) => Object.entries(map).reduce((acc, [k, v]) => acc.split(`%${k}%`).join(v), s || '');

  /* 'MacBook Pro 14" (M1-2021 / M2-M3 2023 / … / M5 Max-2026)' → title 'MacBook Pro 14"', years '2021 · 2023 · …' */
  const title = (name) => name.replace(/\s*\([^)]*\)/g, '').replace(/\s{2,}/g, ' ').trim() || name;
  const years = (name) => [...new Set(name.match(/\b(?:19|20)\d{2}\b/g) || [])].sort().join(' · ');

  /* Apple model numbers (A1234) grow over time: the highest one ranks a device by recency. */
  const rank = (models) => models.reduce((max, m) => Math.max(max, Number((/^A(\d{4})$/.exec(m) || [])[1]) || 0), 0);

  /* Size groups of a device: inches for Macs and iPads, the line for iPhones (a name can cover several). */
  const IPHONE_LINES = ['Pro Max', 'Pro', 'Plus', 'iPhone', 'mini', 'SE'];
  const sizesOf = (name, kind) => {
    const t = title(name);
    if (kind === 'iphone') {
      const lines = new Set();
      t.replace(/^iphone\s*/i, '').split('/').forEach((seg) => {
        const s = seg.toLowerCase();
        if (/pro\s*max/.test(s)) lines.add('Pro Max');
        else if (/\bpro\b/.test(s)) lines.add('Pro');
        else if (/\bplus\b/.test(s)) lines.add('Plus');
        else if (/\bmini\b/.test(s)) lines.add('mini');
        else if (/\bse\b/.test(s)) lines.add('SE');
        else if (s.trim()) lines.add('iPhone');
      });
      return lines.size ? [...lines] : ['other'];
    }
    const inch = Number((/(\d+(?:\.\d+)?)"/.exec(t) || [])[1]);
    if (kind === 'ipad') {
      if (/\bmini\b/i.test(t)) return ['mini'];
      if (!inch) return /ipad\s*\d\b/i.test(t) ? ['9.7"–10.9"'] : ['other'];
      if (inch < 11) return ['9.7"–10.9"'];
      return [`${inch}"`];
    }
    return inch ? [`${inch}"`] : [t.replace(/^macbook\s*/i, '') || 'other'];
  };
  const sizeOrder = (kind) => (a, b) => {
    if (a === 'other') return 1;
    if (b === 'other') return -1;
    if (kind === 'iphone') return IPHONE_LINES.indexOf(a) - IPHONE_LINES.indexOf(b);
    if (kind === 'ipad') { if (a === 'mini') return -1; if (b === 'mini') return 1; }
    const n = (s) => parseFloat(s) || 999;
    return n(a) - n(b) || a.localeCompare(b);
  };

  /* Product type, from the product name (never translated) and handle */
  const TYPES = ['folio', 'envelope', 'pockets', 'slim', 'sleeve'];
  const LIMIT = 6; // rows shown per filter; the rest are on the finder page
  const typeOf = (text) => {
    const s = text.toLowerCase();
    if (/folio|briefcase|laptop[- ]case|carry-all|no\.?\s*7|-s7\b/.test(s)) return 'folio';
    if (/envelope/.test(s)) return 'envelope';
    if (/wallet|organiser|organizer|pocket/.test(s)) return 'pockets';
    if (/slim/.test(s)) return 'slim';
    return 'sleeve';
  };

  /* The metaobjects repeat a display name for different model sets: one entry per name. */
  const group = (list) => {
    const byName = new Map();
    (Array.isArray(list) ? list : []).forEach((d) => {
      const name = String(d.name || '').trim();
      if (!name) return;
      const g = byName.get(name) || { name, models: [], handles: [] };
      (d.models || []).forEach((m) => { m = norm(m); if (m && !g.models.includes(m)) g.models.push(m); });
      (d.handles || []).forEach((h) => { h = String(h || '').trim(); if (h && !g.handles.includes(h)) g.handles.push(h); });
      byName.set(name, g);
    });
    return [...byName.values()].map((g) => ({ ...g, rank: rank(g.models) }));
  };

  let dataPromise = null;
  const loadDevices = (url) => {
    dataPromise = dataPromise || fetch(url, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then((t) => group(JSON.parse(t)))
      .catch((err) => { dataPromise = null; throw err; });
    return dataPromise;
  };

  /* Step 3 data: one light JSON map of every product the device entries point to
   * (/collections/all?view=finder-list, market currency and language), fetched once when the band nears
   * the viewport, so choosing a model renders the list straight away. */
  let listPromise = null;
  const loadList = (root) => {
    listPromise = listPromise || fetch(`${root}/collections/all?view=finder-list`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then((t) => JSON.parse(t))
      .catch(() => { listPromise = null; return {}; });
    return listPromise;
  };
  const fromList = (handle, x) => ({
    handle,
    url: x.u,
    name: x.t,
    img: x.i ? { src: x.i, srcset: x.s || '' } : null,
    price: x.p || '',
    compare: x.c || '',
  });

  /* Fallback for a handle missing from the map (e.g. a product added since the page was cached): its
   * collection card, read for name, link, image and price. */
  const cards = new Map();
  const fetchCard = (root, handle) => {
    if (!cards.has(handle)) {
      cards.set(handle, fetch(`${root}/products/${encodeURIComponent(handle)}?view=finder-card`)
        .then((r) => (r.ok ? r.text() : null))
        .then((html) => (html ? new DOMParser().parseFromString(html, 'text/html').querySelector('.card-product') : null))
        .then((card) => {
          const link = card?.querySelector('.card-product__title a');
          if (!link) return null;
          const img = card.querySelector('.card-product__img:not(.card-product__img--hover)');
          const price = card.querySelector('.card-product__row .price');
          return {
            handle,
            url: link.getAttribute('href'),
            name: link.textContent.trim(),
            img: img ? { src: img.getAttribute('src'), srcset: img.getAttribute('srcset') } : null,
            price: price?.querySelector('.price__current')?.textContent.trim() || '',
            compare: price?.querySelector('s')?.textContent.trim() || '',
          };
        })
        .catch(() => null));
    }
    return cards.get(handle);
  };

  function init(band) {
    let config = {};
    try { config = JSON.parse(band.querySelector('[data-finder-band-config]').textContent); } catch (e) { return; }
    const S = config.strings || {};
    Object.keys(S).forEach((k) => { S[k] = decode(S[k]); });
    const root = String(config.root || '/').replace(/\/$/, '');
    const $ = (sel) => band.querySelector(sel);
    const uid = band.closest('[id]')?.id || 'band';

    const tiles = [...band.querySelectorAll('input[name^="device-"]')].map((input) => ({ input, family: key(input.value), kind: input.dataset.kind, label: input.value, link: input.dataset.link }));
    const stepBtns = [...band.querySelectorAll('[data-goto]')];
    const viewport = $('[data-viewport]');
    const panesEl = $('[data-panes]');
    const panes = [...band.querySelectorAll('[data-pane]')];
    const modelsLoading = $('[data-models-loading]');
    const sizesSet = $('[data-sizes-set]');
    const sizesLegend = $('[data-sizes-legend]');
    const sizesEl = $('[data-sizes]');
    const modelsSet = $('[data-models-set]');
    const modelsList = $('[data-models]');
    const modelsHint = $('[data-models-hint]');
    const browse = $('[data-browse]');
    const lookup = $('[data-lookup]');
    const input = lookup.elements.model;
    const where = $('[data-where]');
    const wherePanel = $('[data-where-panel]');
    const match = $('[data-match]');
    const count = $('[data-count]');
    const deviceName = $('[data-device-name]');
    const deviceYears = $('[data-device-years]');
    const filters = $('[data-filters]');
    const filterStatus = $('[data-filter-status]');
    const track = $('[data-track]');
    const empty = $('[data-empty]');
    const seeAll = $('[data-see-all]');

    let devices = null;
    let index = null;
    let family = null;
    let size = null;
    let current = null;
    let step = 1;
    let maxStep = 1;
    let runId = 0;

    const tileOf = (fam) => tiles.find((t) => t.family === fam);
    const familyOf = (name) => tiles.find((t) => key(name).startsWith(t.family))?.family || null;

    const load = () => { loadList(root); return loadDevices(band.dataset.dataUrl).then((list) => {
      if (!devices) {
        devices = list.map((d) => {
          const fam = familyOf(d.name);
          return { ...d, family: fam, sizes: fam ? sizesOf(d.name, tileOf(fam).kind) : [] };
        });
        index = new Map();
        devices.forEach((d) => d.models.forEach((m) => index.set(m, d)));
        tiles.forEach((t) => {
          const n = devices.filter((d) => d.family === t.family).length;
          const meta = t.input.nextElementSibling?.querySelector('[data-tile-count]');
          if (meta && n) meta.textContent = fill(S.models, { COUNT: n });
        });
      }
      return devices;
    }); };

    /* ------------------------------------------------------------ Steps */
    /* Phones: the band takes the height of the current step (animated). From 750px every step has the
     * same height (CSS, sized for the results step), so nothing jumps. */
    const wide = matchMedia('(min-width: 750px)');
    const fitHeight = () => { viewport.style.height = wide.matches ? '' : `${panes[step - 1].offsetHeight}px`; };
    wide.addEventListener?.('change', fitHeight);
    const ro = new ResizeObserver(() => fitHeight());
    panes.forEach((p) => ro.observe(p));

    const setMax = (n) => {
      maxStep = n;
      stepBtns.forEach((b) => { b.disabled = Number(b.dataset.goto) > maxStep; });
    };

    const goTo = (n, { focus = true } = {}) => {
      n = Math.max(1, Math.min(n, maxStep));
      step = n;
      band.dataset.step = String(n);
      panesEl.style.transform = `translateX(${-(n - 1) * 100}%)`;
      panes.forEach((p, i) => {
        const on = i === n - 1;
        p.inert = !on;
        p.classList.toggle('is-active', on);
      });
      stepBtns.forEach((b) => {
        const k = Number(b.dataset.goto);
        if (k === n) b.setAttribute('aria-current', 'step'); else b.removeAttribute('aria-current');
        b.classList.toggle('is-done', k < n || (k <= maxStep && k !== n));
      });
      band.style.setProperty('--fb-progress', String((n - 1) / (panes.length - 1)));
      fitHeight();
      if (focus) {
        panes[n - 1].querySelector('.finder-band__step-title')?.focus({ preventScroll: true });
        const top = band.querySelector('.finder-band__stepper').getBoundingClientRect().top;
        if (top < 0 || top > innerHeight * 0.6) band.querySelector('.finder-band__stepper').scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      }
    };
    stepBtns.forEach((b) => b.addEventListener('click', () => goTo(Number(b.dataset.goto))));

    /* Swipe between reachable steps on touch screens (the filter chips and fields keep their own gestures) */
    let touch = null;
    viewport.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1 || e.target.closest('.finder-band__filters, input, .finder-band__sizes')) { touch = null; return; }
      touch = { x: e.touches[0].clientX, y: e.touches[0].clientY, dx: 0, axis: null };
    }, { passive: true });
    viewport.addEventListener('touchmove', (e) => {
      if (!touch) return;
      const dx = e.touches[0].clientX - touch.x;
      const dy = e.touches[0].clientY - touch.y;
      if (!touch.axis && Math.abs(dx) + Math.abs(dy) > 10) touch.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (touch.axis !== 'x') return;
      const blocked = (dx > 0 && step === 1) || (dx < 0 && step >= maxStep);
      touch.dx = blocked ? dx / 4 : dx;
      panesEl.classList.add('is-dragging');
      panesEl.style.transform = `translateX(calc(${-(step - 1) * 100}% + ${touch.dx}px))`;
    }, { passive: true });
    const endTouch = () => {
      if (!touch) return;
      panesEl.classList.remove('is-dragging');
      const { dx, axis } = touch;
      touch = null;
      if (axis !== 'x') return;
      const limit = Math.min(80, viewport.clientWidth * 0.2);
      if (dx < -limit && step < maxStep) goTo(step + 1);
      else if (dx > limit && step > 1) goTo(step - 1);
      else goTo(step, { focus: false });
    };
    viewport.addEventListener('touchend', endTouch);
    viewport.addEventListener('touchcancel', endTouch);

    /* ------------------------------------------------------------ Step 2: sizes and models */
    const radio = (name, value, id, checked, label) => {
      const r = document.createElement('input');
      Object.assign(r, { type: 'radio', name, value, id, className: 'visually-hidden', checked });
      if (label) r.setAttribute('aria-label', label);
      return r;
    };

    const renderModels = () => {
      const list = devices.filter((d) => d.family === family && d.sizes.includes(size)).sort((a, b) => b.rank - a.rank || a.name.localeCompare(b.name));
      modelsList.replaceChildren(...list.map((d, i) => {
        const id = `FinderModel-${uid}-${i}`;
        const li = document.createElement('li');
        const label = document.createElement('label');
        label.htmlFor = id;
        label.className = 'finder-band__model';
        const t = document.createElement('span');
        t.className = 'finder-band__model-title';
        t.textContent = title(d.name);
        label.append(t);
        const y = years(d.name);
        if (y) {
          const sm = document.createElement('span');
          sm.className = 'finder-band__model-years';
          sm.textContent = y;
          label.append(sm);
        }
        li.append(radio(`model-${uid}`, d.name, id, current?.name === d.name, d.name), label);
        return li;
      }));
      modelsSet.hidden = !list.length;
      modelsHint.hidden = true;
    };

    const renderSizes = () => {
      const tile = tileOf(family);
      const all = [...new Set(devices.filter((d) => d.family === family).flatMap((d) => d.sizes))].sort(sizeOrder(tile.kind));
      if (!all.includes(size)) size = all.length === 1 ? all[0] : null;
      sizesLegend.textContent = tile.kind === 'iphone' ? S.line : S.size;
      sizesEl.replaceChildren(...all.map((s, i) => {
        const id = `FinderSize-${uid}-${i}`;
        const wrap = document.createElement('span');
        const label = document.createElement('label');
        label.htmlFor = id;
        label.className = 'finder-band__size';
        label.textContent = s === 'other' ? S.other : s;
        wrap.append(radio(`size-${uid}`, s, id, s === size), label);
        return wrap;
      }));
      sizesSet.hidden = !all.length;
      if (size) renderModels();
      else { modelsSet.hidden = true; modelsList.replaceChildren(); modelsHint.hidden = false; }
    };

    const showFamily = async (fam) => {
      family = fam;
      band.dataset.family = fam || '';
      const tile = tileOf(fam);
      band.dataset.kind = tile?.kind || '';
      tiles.forEach((t) => { t.input.checked = t === tile; });
      if (tile?.link) {
        browse.href = tile.link;
        browse.textContent = fill(S.browse, { FAMILY: tile.label });
        browse.hidden = false;
      } else browse.hidden = true;
      if (!fam) { sizesSet.hidden = true; modelsSet.hidden = true; modelsHint.hidden = true; return; }
      if (current && current.family === fam) size = current.sizes?.[0] || size;
      else size = null;
      if (!devices) { modelsLoading.hidden = false; sizesSet.hidden = true; modelsSet.hidden = true; }
      try { await load(); } catch (e) {
        modelsLoading.hidden = true;
        match.textContent = S.error;
        return;
      }
      modelsLoading.hidden = true;
      if (family !== fam) return;
      if (current && current.family === fam) size = current.sizes?.find((s) => s === size) || current.sizes?.[0] || size;
      renderSizes();
    };

    /* ------------------------------------------------------------ Step 3: results */
    const skeleton = () => {
      const li = document.createElement('li');
      li.className = 'finder-band__row finder-band__row--skel';
      li.setAttribute('aria-hidden', 'true');
      li.innerHTML = '<span class="skeleton finder-band__thumb"></span><span class="finder-band__row-text"><span class="skeleton finder-band__skel-line"></span><span class="skeleton finder-band__skel-line finder-band__skel-line--short"></span></span>';
      return li;
    };

    let activeType = 'all';
    const applyFilter = (type) => {
      activeType = type;
      const items = [...track.children];
      let matches = 0;
      items.forEach((li) => {
        const on = type === 'all' || li.dataset.type === type;
        if (on) matches += 1;
        li.hidden = !on || matches > LIMIT;
      });
      filters.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.type === type)));
      filterStatus.textContent = fill(S.filterCount, { COUNT: matches, TOTAL: items.length });
      fitHeight();
    };

    const renderFilters = (types) => {
      const present = TYPES.filter((t) => types.includes(t));
      filters.hidden = present.length < 2;
      if (filters.hidden) { filters.replaceChildren(); return; }
      const btn = (type, text) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'finder-band__filter';
        b.dataset.type = type;
        b.textContent = text;
        b.setAttribute('aria-pressed', String(type === 'all'));
        b.addEventListener('click', () => applyFilter(type));
        return b;
      };
      filters.replaceChildren(btn('all', S.filterAll), ...present.map((t) => btn(t, S[`filter_${t}`] || t)));
    };

    /* One list row: thumbnail on white, name, type, price and an arrow; the whole row links */
    const row = (p) => {
      const li = document.createElement('li');
      li.className = 'finder-band__row';
      li.dataset.type = typeOf(`${p.name} ${p.handle}`);
      const a = document.createElement('a');
      a.className = 'finder-band__row-link';
      a.href = p.url;
      const thumb = document.createElement('span');
      thumb.className = 'finder-band__thumb';
      if (p.img?.src) {
        const img = document.createElement('img');
        img.src = p.img.src;
        if (p.img.srcset) { img.srcset = p.img.srcset; img.sizes = '72px'; }
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.width = 72;
        img.height = 72;
        thumb.append(img);
      }
      const text = document.createElement('span');
      text.className = 'finder-band__row-text';
      const nm = document.createElement('span');
      nm.className = 'finder-band__row-name';
      nm.textContent = p.name;
      const tp = document.createElement('span');
      tp.className = 'finder-band__row-type';
      tp.textContent = S[`type_${li.dataset.type}`] || '';
      text.append(nm, tp);
      const price = document.createElement('span');
      price.className = 'finder-band__row-price';
      price.textContent = p.price;
      if (p.compare) { const s = document.createElement('s'); s.textContent = p.compare; price.append(' ', s); }
      a.append(thumb, text, price);
      a.insertAdjacentHTML('beforeend', '<svg class="finder-band__row-arrow" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>');
      li.append(a);
      return li;
    };

    const showResults = async (device) => {
      const run = ++runId;
      const name = title(device.name);
      deviceName.textContent = name;
      deviceYears.textContent = years(device.name);
      deviceName.title = device.name;
      empty.hidden = true;
      seeAll.hidden = true;
      filters.hidden = true;
      count.textContent = S.loading;
      track.replaceChildren(...Array.from({ length: LIMIT }, skeleton));

      const list = await loadList(root);
      const found = (await Promise.all(device.handles.map((h) => (list[h] ? fromList(h, list[h]) : fetchCard(root, h))))).filter(Boolean);
      if (run !== runId) return;
      const link = `${band.dataset.finderUrl}?model=${encodeURIComponent(device.models[0] || '')}`;
      seeAll.href = link;
      if (!found.length) {
        track.replaceChildren();
        count.textContent = name;
        empty.hidden = false;
        seeAll.textContent = fill(S.seeAll, { COUNT: 0, DEVICE: name });
        seeAll.hidden = false;
        fitHeight();
        return;
      }
      const items = found.map(row);
      track.replaceChildren(...items);
      renderFilters(items.map((li) => li.dataset.type));
      applyFilter('all');
      count.textContent = found.length === 1
        ? fill(S.resultsOne, { DEVICE: name })
        : fill(S.resultsOther, { COUNT: found.length, DEVICE: name });
      seeAll.textContent = fill(S.seeAll, { COUNT: found.length, DEVICE: name });
      seeAll.hidden = false;
      requestAnimationFrame(fitHeight);
    };

    const choose = (device, { remember = true, focus = true } = {}) => {
      current = device;
      if (remember) {
        store.set({ name: device.name, models: device.models, handles: device.handles, family: device.family || null });
        window.dispatchEvent(new CustomEvent('device:changed', { detail: device }));
      }
      modelsList.querySelectorAll('input').forEach((r) => { r.checked = r.value === device.name; });
      setMax(3);
      showResults(device);
      goTo(3, { focus });
    };

    /* ------------------------------------------------------------ Events
     * A tap or click on a choice moves on to the next step. With the keyboard, arrow keys only move the
     * selection (as radios do) and Enter or Space moves on. */
    let lastPointer = 0;
    band.addEventListener('pointerdown', () => { lastPointer = Date.now(); }, true);
    const byPointer = () => Date.now() - lastPointer < 1000;
    const onConfirmKey = (container, fn) => container.addEventListener('keydown', (e) => {
      if ((e.key === 'Enter' || e.key === ' ') && e.target.matches('input[type="radio"]')) {
        e.preventDefault();
        e.target.checked = true;
        e.target.dispatchEvent(new Event('change', { bubbles: true }));
        fn(e.target);
      }
    });

    const pickFamily = (t) => {
      if (current && current.family !== t.family) { current = null; runId += 1; }
      setMax(current ? 3 : 2);
      if (family !== t.family || !devices) showFamily(t.family);
    };
    tiles.forEach((t) => {
      t.input.addEventListener('change', () => {
        if (!t.input.checked) return;
        pickFamily(t);
        if (byPointer()) goTo(2);
      });
      /* Choosing the tile that is already selected still moves on */
      t.input.nextElementSibling.addEventListener('click', () => {
        if (t.input.checked && family === t.family) setTimeout(() => goTo(2), 0);
      });
    });
    onConfirmKey(band.querySelector('.finder-band__tiles'), () => setTimeout(() => goTo(2), 0));

    sizesEl.addEventListener('change', (e) => {
      const r = e.target.closest('input[type="radio"]');
      if (!r) return;
      size = r.value;
      renderModels();
    });

    const pickModel = (r) => {
      const device = devices?.find((d) => d.name === r.value);
      if (!device) return;
      input.value = '';
      match.textContent = '';
      choose(device);
    };
    modelsList.addEventListener('change', (e) => {
      const r = e.target.closest('input[type="radio"]');
      if (r && byPointer()) pickModel(r);
    });
    modelsList.addEventListener('click', (e) => {
      const label = e.target.closest('label');
      const r = label && document.getElementById(label.htmlFor);
      if (r?.checked && current?.name === r.value) setTimeout(() => pickModel(r), 0);
    });
    onConfirmKey(modelsList, (r) => pickModel(r));

    /* Model number, matched as it is typed (like the finder page: exact, or with the A prefix) */
    let typeTimer;
    const lookupModel = (final) => {
      const model = norm(input.value);
      if (!model) { match.textContent = ''; match.classList.remove('is-miss', 'is-hit'); return; }
      if (model.length < 4 && !final) return;
      load().then(() => {
        if (norm(input.value) !== model) return;
        const device = index.get(model) || index.get(model.replace(/^(?!A)/, 'A'));
        match.classList.toggle('is-hit', !!device);
        match.classList.toggle('is-miss', !device && (final || model.length >= 5));
        if (!device) {
          match.textContent = final || model.length >= 5 ? S.noMatch : '';
          if (final || model.length >= 5) document.dispatchEvent(new CustomEvent('finder:miss', { detail: { model } })); // Insights: devices we have no sleeve for
          return;
        }
        match.textContent = fill(S.match, { DEVICE: device.name });
        if (current?.name === device.name && step === 3) return;
        current = device;
        if (device.family) {
          size = device.sizes[0];
          if (device.family !== family) showFamily(device.family); else renderSizes();
        }
        setTimeout(() => choose(device), final ? 0 : 600); // let the shopper see the match first
      }).catch(() => { match.textContent = S.error; });
    };
    input.addEventListener('input', () => { clearTimeout(typeTimer); typeTimer = setTimeout(() => lookupModel(false), 250); });
    lookup.addEventListener('submit', (e) => { e.preventDefault(); clearTimeout(typeTimer); lookupModel(true); });

    where.addEventListener('click', () => {
      const open = wherePanel.hidden;
      if (window.Theme?.slide) window.Theme.slide(wherePanel, open); else wherePanel.hidden = !open;
      where.setAttribute('aria-expanded', String(open));
    });
    lookup.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !wherePanel.hidden) { if (window.Theme?.slide) window.Theme.slide(wherePanel, false); else wherePanel.hidden = true; where.setAttribute('aria-expanded', 'false'); where.focus(); }
    });

    $('[data-change]').addEventListener('click', () => {
      setMax(family ? 2 : 1);
      goTo(family ? 2 : 1);
    });

    /* ------------------------------------------------------------ Start */
    goTo(1, { focus: false });
    requestAnimationFrame(() => band.classList.add('is-ready'));
    const saved = store.get();
    let restoring = false;
    if (saved) {
      const fam = saved.family || familyOf(saved.name);
      if (tileOf(fam)) { tileOf(fam).input.checked = true; band.dataset.family = fam; band.dataset.kind = tileOf(fam).kind; family = fam; }
      // Land on step 3 right away; the cards fill in once the band nears the viewport
      restoring = true;
      current = { ...saved, family: fam, sizes: fam ? sizesOf(saved.name, tileOf(fam).kind) : [] };
      setMax(3);
      deviceName.textContent = title(saved.name);
      deviceYears.textContent = years(saved.name);
      count.textContent = S.loading;
      track.replaceChildren(...Array.from({ length: LIMIT }, skeleton));
      goTo(3, { focus: false });
    }

    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      load().then(() => {
        if (!restoring) return;
        restoring = false;
        const device = devices.find((d) => d.name === current.name) || current;
        current = device;
        if (device.family) showFamily(device.family);
        if (step === 3) { setMax(3); showResults(device); }
      }).catch(() => { started = false; });
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { io.disconnect(); start(); }
      }, { rootMargin: '600px 0px' });
      io.observe(band);
    } else start();
    // First sight: the devices slip into their sleeves once (staggered), to show what the tiles do
    if ('IntersectionObserver' in window && !reduced) {
      const seen = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { seen.disconnect(); band.classList.add('is-seen'); }
      }, { threshold: 0.45 });
      seen.observe(band.querySelector('.finder-band__tiles') || band);
    }
    band.addEventListener('focusin', start, { once: true });
    band.addEventListener('pointerdown', start, { once: true });
  }

  const run = () => document.querySelectorAll('[data-finder-band]:not([data-ready])').forEach((band) => {
    band.dataset.ready = '';
    init(band);
  });
  run();
  document.addEventListener('shopify:section:load', run);
})();
